import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DescriptionIcon from '@mui/icons-material/Description'
import CalculateIcon from '@mui/icons-material/Calculate'
import BusinessIcon from '@mui/icons-material/Business'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import StorageIcon from '@mui/icons-material/Storage'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import KeyboardIcon from '@mui/icons-material/Keyboard'

const KeyboardShortcutsContext = createContext()

const commands = [
  { id: 'dashboard', label: 'Go to Dashboard', path: '/', icon: <DashboardIcon />, keywords: ['home', 'overview', 'stats'] },
  { id: 'case-studies', label: 'Go to Case Studies', path: '/case-studies', icon: <DescriptionIcon />, keywords: ['studies', 'data', 'table'] },
  { id: 'roi', label: 'Go to ROI Calculator', path: '/roi-calculator', icon: <CalculateIcon />, keywords: ['calculate', 'savings', 'payback'] },
  { id: 'fleet', label: 'Go to Fleet Impact', path: '/fleet-impact', icon: <BusinessIcon />, keywords: ['portfolio', 'fleet', 'multiple'] },
  { id: 'compare', label: 'Go to Compare', path: '/compare', icon: <CompareArrowsIcon />, keywords: ['comparison', 'side by side'] },
  { id: 'analysis', label: 'Go to Analysis', path: '/analysis', icon: <AnalyticsIcon />, keywords: ['research', 'statistics', 'correlation'] },
  { id: 'data-explorer', label: 'Go to Data Explorer', path: '/data-explorer', icon: <StorageIcon />, keywords: ['explore', 'measurements', 'corpus'] },
  { id: 'ai', label: 'Go to AI Assistant', path: '/ai-assistant', icon: <SmartToyIcon />, keywords: ['chat', 'ask', 'question'] },
]

const shortcuts = [
  { keys: ['Ctrl', 'K'], action: 'Open command palette' },
  { keys: ['Ctrl', '/'], action: 'Show keyboard shortcuts' },
  { keys: ['Escape'], action: 'Close dialogs/drawers' },
  { keys: ['Ctrl', 'E'], action: 'Go to Data Explorer' },
  { keys: ['Ctrl', 'D'], action: 'Go to Dashboard' },
]

export function KeyboardShortcutsProvider({ children }) {
  const navigate = useNavigate()
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Filter commands based on search
  const filteredCommands = commands.filter(cmd => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      cmd.label.toLowerCase().includes(searchLower) ||
      cmd.keywords.some(k => k.includes(searchLower))
    )
  })

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [search])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K - Open command palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(true)
        setSearch('')
      }

      // Ctrl+/ - Show shortcuts help
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault()
        setShortcutsHelpOpen(true)
      }

      // Ctrl+E - Data Explorer
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault()
        navigate('/data-explorer')
      }

      // Ctrl+D - Dashboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        navigate('/')
      }

      // Escape - Close dialogs
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false)
        setShortcutsHelpOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  // Handle command palette keyboard navigation
  const handlePaletteKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
      e.preventDefault()
      executeCommand(filteredCommands[selectedIndex])
    }
  }

  const executeCommand = useCallback((cmd) => {
    navigate(cmd.path)
    setCommandPaletteOpen(false)
    setSearch('')
  }, [navigate])

  const openCommandPalette = useCallback(() => {
    setCommandPaletteOpen(true)
    setSearch('')
  }, [])

  return (
    <KeyboardShortcutsContext.Provider value={{ openCommandPalette }}>
      {children}

      {/* Command Palette */}
      <Dialog
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { position: 'fixed', top: '15%', m: 0 }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handlePaletteKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip label="esc" size="small" variant="outlined" />
                </InputAdornment>
              ),
              sx: { fontSize: '1.1rem' }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                '& fieldset': { border: 'none' }
              }
            }}
          />
          <Divider />
          <List dense sx={{ maxHeight: 300, overflow: 'auto', py: 1 }}>
            {filteredCommands.map((cmd, index) => (
              <ListItem key={cmd.id} disablePadding>
                <ListItemButton
                  selected={index === selectedIndex}
                  onClick={() => executeCommand(cmd)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& .MuiListItemIcon-root': { color: 'white' }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {cmd.icon}
                  </ListItemIcon>
                  <ListItemText primary={cmd.label} />
                </ListItemButton>
              </ListItem>
            ))}
            {filteredCommands.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No commands found
              </Typography>
            )}
          </List>
        </DialogContent>
      </Dialog>

      {/* Shortcuts Help Dialog */}
      <Dialog
        open={shortcutsHelpOpen}
        onClose={() => setShortcutsHelpOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyboardIcon />
          Keyboard Shortcuts
        </DialogTitle>
        <DialogContent>
          <List dense>
            {shortcuts.map((shortcut, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.5, minWidth: 120 }}>
                  {shortcut.keys.map((key, i) => (
                    <Chip
                      key={i}
                      label={key}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', minWidth: 40 }}
                    />
                  ))}
                </Box>
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {shortcut.action}
                </Typography>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </KeyboardShortcutsContext.Provider>
  )
}

export function useKeyboardShortcuts() {
  const context = useContext(KeyboardShortcutsContext)
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within KeyboardShortcutsProvider')
  }
  return context
}
